import { width } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import OnboardingTutorial from 'components/OnboardingTutorial';
import { useHubspotTrackingCode } from 'hooks';
import React, { FunctionComponent } from 'react';
import { wbp } from 'Theme';
import { getPaymentSlides } from './Slides';

interface IntroductionProps {
    className?: string;
    enrollmentStarted?: boolean;
    onAddCuddlesPay?: () => void;
    onLearnMore?: () => void;
    isButtonDisabled: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            minHeight: '100%',
            height: '100%'
        },
        gridContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '90px',
            [theme.breakpoints.down(1025)]: {
                gap: '40px',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                paddingTop: '40px',
                paddingLeft: '15px',
                paddingRight: '15px'
            },
            [theme.breakpoints.down(370)]: {
                flexDirection: 'row',
                paddingBottom: '20px'
            }
        },
        left: {
            alignItems: 'center',
            [theme.breakpoints.up('md')]: {
                display: 'flex',
                justifyContent: 'center'
            },
            [theme.breakpoints.down(1025)]: {
                height: 'auto',
                minHeight: 'auto'
            },
            [theme.breakpoints.down(370)]: {
                width: '100%',
                textAlign: 'center'
            }
        },
        right: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            [theme.breakpoints.up('md')]: {
                alignItems: 'flex-start'
            },
            [theme.breakpoints.down(1025)]: {
                alignItems: 'flex-start',
                minHeight: 'auto'
            }
        },
        body: {
            width: '100%',
            maxWidth: '506px'
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            marginTop: '40px',
            [theme.breakpoints.down('md')]: {
                justifyContent: 'flex-start'
            },
            [theme.breakpoints.up('md')]: {
                justifyContent: 'flex-start'
            },
            [theme.breakpoints.down(370)]: {
                paddingBottom: '30px'
            }
        },
        image: {
            width: '38vw',
            [theme.breakpoints.up('md')]: {
                width: '35vw'
            },
            [theme.breakpoints.up('xl')]: {
                width: '30vw'
            }
        },
        title: {
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#EAB464',
            fontSize: '16px',
            lineHeight: '27px'
        },
        subtitle: {
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#222222',
            letterSpacing: '-0.02em',
            fontSize: '32px',
            lineHeight: '130%',
            marginTop: '6px',
            marginBottom: '15px',
            maxWidth: '420px',
            [theme.breakpoints.down('md')]: {
                fontSize: '26px',
                lineHeight: '30px',
                marginTop: '5px'
            }
        },
        firstTextLine: {
            display: 'flex',
            marginTop: '30px'
        },
        textLine: {
            display: 'flex',
            [theme.breakpoints.down(wbp)]: {
                marginTop: '25px'
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '25px'
            }
        },
        textLineWider: {
            maxWidth: '435px'
        },
        text: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            color: '#222222',
            fontSize: '16px',
            lineHeight: '130%'
        },
        check: {
            color: '#92B4A7',
            display: 'flex',
            alignItems: 'flex-start',
            margin: '0px 10px 0px 0px',
            width: '20px !important',
            height: '20px !important'
        },
        button: {
            [theme.breakpoints.down(wbp)]: {
                marginRight: '16px'
            },
            [theme.breakpoints.up(wbp)]: {
                marginRight: '20px'
            },
            [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                height: '40px',
                padding: '10px 20px'
            }
        },
        learnMore: {
            '& .MuiButton-label': {
                fontSize: '14px'
            }
        },
        onboardingContainer: {
            height: '100%',
            width: '100%'
        }
    })
);

export const Introduction: FunctionComponent<IntroductionProps> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    //const auth = useAuth();
    //const marketplaceId = store.getState().login.auth.marketplaceId;

    const { setTrackEvent } = useHubspotTrackingCode();

    const check = (
        <FontAwesomeIcon
            icon={faCheckCircle}
            className={classes.check}
        />
    );

    const getStartedHandler = () => {
        setTrackEvent(`${process.env.REACT_APP_HUBSPOT_EVENT_CUDDLESPAY_GET_STARTED}`);
        props.onAddCuddlesPay && props.onAddCuddlesPay();
        /*
        Manual CuddlesPay automation flow for new accounts
        const userEmail = auth.user?.email || '';
        window.open(
            `https://cuddlesapp.com/contact-us?marketplace_id=${marketplaceId}&email=${encodeURIComponent(userEmail)}`
        );
        */
    };

    const learnMoreHandler = () => {
        setTrackEvent(`${process.env.REACT_APP_HUBSPOT_EVENT_CUDDLESPAY_LEARN}`);
        window.open(process.env.REACT_APP_PAYMENTS_LEARN_MORE_URL);
    };

    const primaryButtonLabel = props.enrollmentStarted ? 'Continue Process' : 'Get Started';

    const paymentSlides = getPaymentSlides(
        primaryButtonLabel,
        props.isButtonDisabled,
        isMobile,
        getStartedHandler,
        learnMoreHandler
    );

    const onboardingPageParams = {
        title: 'Payments',
        imageWidth: 300,
        imageHeight: 500
    };

    const onBoarding = (
        <Box className={classes.onboardingContainer}>
            <OnboardingTutorial
                id="payments_onboarding"
                titlePage={onboardingPageParams.title}
                imageWidth={onboardingPageParams.imageWidth}
                imageHeight={onboardingPageParams.imageHeight}
                slides={paymentSlides}
            />
        </Box>
    );

    return <>{onBoarding}</>;
};

export default Introduction;
